import { useMemo, useState } from 'react'
import {
  FlowBalanceChartGroupedTransactions,
  FlowBalanceChartProps
} from './FlowBalanceChart.types'

export const useChartNavigation = (data: FlowBalanceChartProps['data']) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const onNextClick = () => {
    setCurrentIndex((previousIndex) =>
      Math.min(previousIndex + 1, data.length - 1)
    )
  }

  const onPrevClick = () => {
    setCurrentIndex((previousIndex) => Math.max(previousIndex - 1, 0))
  }

  const options = data.map(([value]) => ({ value }))

  const disabledArrow = (() => {
    if (data.length > 1) {
      if (currentIndex === 0) {
        return 'prev' as const
      }

      if (currentIndex === data.length - 1) {
        return 'next' as const
      }
    }
  })()

  return {
    disabled: data.length < 2,
    disabledArrow,
    options,
    currentIndex,
    onPrevClick,
    onNextClick
  }
}

export const useProcessedTransactions = (
  transactions: FlowBalanceChartGroupedTransactions[1]
) => {
  return useMemo(() => {
    const movements = transactions.movements.reduce(
      (result, movementData) => {
        return [
          ...result,
          {
            label: movementData[0],
            valueIn: movementData[1],
            valueOut: movementData[2],
            balance:
              movementData[1] -
              movementData[2] +
              (result[result.length - 1]?.balance ??
                transactions.initial_balance.value)
          }
        ]
      },
      [] as {
        label: string
        valueIn: number
        valueOut: number
        balance: number
      }[]
    )

    const movementInValues = movements.map((movement) => movement.valueIn)
    const movementOutValues = movements.map((movement) => movement.valueOut)

    const lowestMovement = Math.min(...movementInValues, ...movementOutValues)
    const highestMovement = Math.max(...movementInValues, ...movementOutValues)
    const totalMovementIn = movementInValues.reduce((a, b) => a + b, 0)
    const totalMovementOut = movementOutValues.reduce((a, b) => a + b, 0)

    return {
      initialBalance: transactions.initial_balance,
      movements,
      lowestMovement,
      highestMovement,
      totalMovementIn,
      totalMovementOut,
      finalBalance: transactions.final_balance
    }
  }, [
    transactions.final_balance,
    transactions.initial_balance,
    transactions.movements
  ])
}
